import type { AppShell } from "@/App"
import type { TimelineEvent } from "@/components/timeline"
import type { StepStates } from "@/components/steps"

export interface OrderRow {
  id: string
  shopify_order_id: string
  shopify_order_number: string | null
  customer_name: string | null
  customer_email: string | null
  customer_nif: string | null
  total_amount: number | string | null
  currency: string | null
  items_count: number | null
  weight_kg: number | string | null
  /** Shipping cost (gross) → ETICDATA "portes" line. */
  shipping_amount: number | string | null
  // Shipping (→ Correos Expresso)
  ship_name: string | null
  ship_address: string | null
  ship_city: string | null
  ship_zip: string | null
  ship_country: string | null
  ship_phone: string | null
  // Billing (→ ETICDATA / faturação)
  bill_name: string | null
  bill_company: string | null
  bill_address: string | null
  bill_city: string | null
  bill_zip: string | null
  bill_country: string | null
  bill_phone: string | null
  current_step: string | null
  step_states: StepStates | null
  next_action: string | null
  awaiting_supplier: boolean | null
  /** Payload Shopify cru — usado para derivar forma de pagamento / levantamento. */
  raw_payload?: unknown
  /** Notificação de atraso ao cliente (email próprio). */
  delay_notified_at: string | null
  delay_eta: string | null
  delay_note: string | null
  completed_at: string | null
  /** Alterações vindas do Shopify (v0.25). */
  shopify_changed_at?: string | null
  cancelled_at?: string | null
  change_alert?: string | null
  /** Levantamento em loja (v0.26). */
  pickup_notified_at?: string | null
  pickup_ready_at?: string | null
  /** Portes a orçamentar (v0.28). */
  shipping_quote_required_at?: string | null
  shipping_quote_paid_at?: string | null
  shipping_quote_amount?: number | string | null
  shipping_quote_requested_at?: string | null
  /** Edição com a mesma ENC: alterações por passar ao ETICDATA (v0.30). */
  enc_update_pending?: string | null
  enc_update_pending_at?: string | null
  /** Edição por pagar: separação suspensa até o pagamento da diferença. */
  payment_pending_at?: string | null
  /** Última edição do Shopify, por SKU (minúsculas): antes/depois. */
  line_changes?: unknown
  /** Secção do ETICDATA = fonte (4 online, 1 balcão, 2 comercial, 3 exportação). NULL = online. */
  source_section?: string | null
  /** Encomendas do ETICDATA: entrega decidida na importação (pickup | carrier | own). */
  erp_delivery?: string | null
  /** Referência do orçamento escrita na ENCCL (ex.: "ORC 88426/65"). */
  erp_origin_ref?: string | null
  /** Forma de pagamento indicada à mão (o ERP não a guarda). */
  payment_method_manual?: string | null
  picked_up_at?: string | null
  created_at: string
  updated_at: string
}

export interface OrderItem {
  id: string
  order_id: string
  sku: string
  name: string | null
  qty: number
  /** Quantidade exata do ERP quando é decimal (1,5 m); `qty` vem arredondada para cima. */
  qty_exact?: number | string | null
  unit_price: number | string | null
  weight_kg: number | string | null
  /** Shopify vendor = brand (for the logistics "Estado" column). */
  vendor: string | null
  /** Warehouse separation (logistics): set when the line is picked. */
  separated_at: string | null
  separated_by: string | null
}

export interface StockCheck {
  id: string
  order_id: string
  sku: string
  /** Reference actually queried in ETICDATA. Equal to `sku` → brand code
   *  missing; `<external_code>.<sku>` → composed OK. */
  eticdata_ref: string | null
  matched: boolean
  available_qty: number | null
  ordered_qty: number
  missing_qty: number
  /** Product thumbnail (CRM catalog). */
  image_url: string | null
  /** Preço de CUSTO do CRM — é o que se envia ao criar o artigo no ETICDATA. */
  cost_price: number | string | null
  /** Curated public name (CRM catalog alt_name), when set. Pre-fills the
   *  "Criar artigos" popup name field. */
  alt_name: string | null
  supplier_hint: string | null
  /** Diagnostic JSON from the host's ZLINK lookup (debug). */
  raw_response: {
    queried_sku?: string
    warehouse?: string
    hub_success?: boolean
    hub_error?: string
    returned_count?: number
    returned_refs?: string[]
    exact_match?: boolean
  } | null
  checked_at: string
}

export interface SupplierOrder {
  id: string
  order_id: string
  supplier_name: string
  supplier_code: string | null
  brand: string | null
  status: "draft" | "approved" | "sent" | "received" | "skipped"
  total_items: number
  approved_by: string | null
  approved_at: string | null
  sent_at: string | null
  external_ref: string | null
}

export interface SupplierOrderItem {
  id: string
  supplier_order_id: string
  order_id: string
  sku: string
  qty: number
}

export interface Expedition {
  id: string
  order_id: string
  carrier: string | null
  tracking_number: string | null
  provider: string | null
  reference: string | null
  status: string | null
  packages: number | null
  pickup_number: string | null
  pickup_date: string | null
  pickup_cancelled_at: string | null
  /** R2 reference to the carrier label (the base64 is NOT stored in the DB). */
  label_pdf_url: string | null
  label_storage_key: string | null
  label_format: string | null
  label_size: number | null
  /** Set once the tracking is pushed to Shopify (customer notified). */
  shopify_fulfilled_at: string | null
  created_at: string
}

// ─── Ajustes de armazém (linha) + comentários da encomenda ──────────────────

/** Vocabulário do dropdown de estado do ajuste. Informativo — NÃO altera a
 *  ENC nem a fatura no ETICDATA. */
export const ADJUSTMENT_STATUSES = [
  { value: "damaged", label: "Danificado" },
  { value: "missing", label: "Sem stock / Em falta" },
  { value: "other", label: "Outro" },
] as const

export type AdjustmentStatus = (typeof ADJUSTMENT_STATUSES)[number]["value"]

export function adjustmentStatusLabel(status: string): string {
  return ADJUSTMENT_STATUSES.find((s) => s.value === status)?.label ?? status
}

export interface OrderItemAdjustment {
  id: string
  order_id: string
  order_item_id: string
  qty: number
  status: string
  comment: string | null
  /** auth user id — resolver o nome com `listAppUsers` (fallback p/ linhas antigas). */
  created_by: string | null
  /** Nome carimbado no write (fonte autoritativa /context). */
  created_by_name: string | null
  created_at: string
}

export interface OrderComment {
  id: string
  order_id: string
  body: string
  created_by: string | null
  created_by_name: string | null
  created_at: string
}

// ─── Nº ENC do ETICDATA + pagamento / levantamento em loja ───────────────────

/**
 * Nº ENC atribuído pelo ETICDATA (ex: "ENCCL 112426/606").
 *
 * Quando o ZLINK Hub não devolve o `document_number`, fica guardada a nossa
 * referência sintética (`KTX-…`), que NÃO é um nº ETICDATA — devolve null.
 * (Espelha `realEncNumber` do engine.)
 */
/** Encomenda importada do ETICDATA (balcão/comercial/exportação) — sem Shopify. */
export function isErpOrder(order: { shopify_order_id?: string | null }): boolean {
  return String(order.shopify_order_id ?? "").startsWith("etic:")
}

export function realEncNumber(doc: string | null | undefined): string | null {
  const d = (doc ?? "").trim()
  if (!d || /^KTX-/i.test(d) || /^K\d{1,7}R\d+$/i.test(d)) return null
  return d
}

/**
 * Sufixo de marca no FIM do nome do artigo (ex: "… 2700K SYL").
 * Mapa configurável `brand_name_suffix_map`; sem entrada usa as 3 primeiras
 * letras da marca. (Espelha `brandNameSuffix` do engine.)
 */
export function brandNameSuffix(
  brandName: string | null | undefined,
  map: Record<string, unknown>,
): string | null {
  const raw = (brandName ?? "").trim()
  if (!raw) return null
  const mapped = map?.[raw.toUpperCase()] ?? map?.[raw]
  if (mapped != null && String(mapped).trim() !== "") return String(mapped).trim().toUpperCase()
  const letters = raw
    .normalize("NFD")
    .replace(new RegExp("[\\u0300-\\u036f]", "g"), "")
    .replace(/[^A-Za-z]/g, "")
  return letters ? letters.slice(0, 3).toUpperCase() : null
}

/** Acrescenta o sufixo da marca ao fim do nome, sem duplicar. */
export function applyBrandSuffix(
  name: string,
  brandName: string | null | undefined,
  map: Record<string, unknown>,
): string {
  const suffix = brandNameSuffix(brandName, map)
  if (!suffix) return name
  const trimmed = name.trim()
  if (new RegExp(`(^|[^A-Za-z0-9])${suffix}$`, "i").test(trimmed)) return trimmed
  return `${trimmed} ${suffix}`
}

export interface OrderPaymentInfo {
  method: string | null
  paid: boolean
  pickupInStore: boolean
}

/** Forma de pagamento + levantamento em loja, do payload Shopify guardado.
 *  (Espelha `resolvePaymentInfo` do engine.) */
/** Espelho de functions/lib/engine/orders.ts:paymentMethodPt. */
export function paymentMethodPt(raw: string): string {
  const s = raw.trim().toLowerCase()
  if (/bank.?deposit|bank.?transfer|transfer[êe]ncia/.test(s)) return "Transferência bancária"
  if (/shopify.?payments/.test(s)) return "Cartão (Shopify Payments)"
  if (/mb.?way/.test(s)) return "MB WAY"
  if (/multibanco|\bmb\b|ifthenpay|eupago|easypay/.test(s)) return "Multibanco"
  if (/paypal/.test(s)) return "PayPal"
  if (/cash.?on.?delivery|\bcod\b|cobran[çc]a/.test(s)) return "Pagamento na entrega"
  if (/gift.?card/.test(s)) return "Cartão oferta"
  if (/^manual$/.test(s)) return "Manual"
  return raw.trim().replace(/_/g, " ")
}

export function resolvePaymentInfo(order: OrderRow): OrderPaymentInfo {
  // Encomenda do ETICDATA: considerada paga; BALCÃO (secção 1) é sempre
  // levantamento na loja. (Espelha o engine — ERP_PICKUP_SECTIONS.)
  if (isErpOrder(order)) {
    return {
      method: String(order.payment_method_manual ?? "").trim() || null,
      paid: true,
      pickupInStore: String(order.source_section ?? "") === "1" || order.erp_delivery === "pickup",
    }
  }
  const rp = order.raw_payload
  let p: Record<string, unknown> | null = null
  if (rp && typeof rp === "string") {
    try {
      p = JSON.parse(rp) as Record<string, unknown>
    } catch {
      p = null
    }
  } else if (rp && typeof rp === "object") {
    p = rp as Record<string, unknown>
  }
  if (!p) return { method: null, paid: false, pickupInStore: false }

  const names = Array.isArray((p as { payment_gateway_names?: unknown }).payment_gateway_names)
    ? (p as { payment_gateway_names: unknown[] }).payment_gateway_names
        .map((n) => String(n).trim())
        .filter(Boolean)
    : []
  const gateway = String((p as { gateway?: unknown }).gateway ?? "").trim()
  const rawNames = names.length > 0 ? names : gateway ? [gateway] : []
  const method = rawNames.length > 0 ? rawNames.map(paymentMethodPt).join(" + ") : null
  const paid =
    String((p as { financial_status?: unknown }).financial_status ?? "").toLowerCase() === "paid"

  const lines = (p as { shipping_lines?: Array<Record<string, unknown>> }).shipping_lines ?? []
  const hay = lines
    .map((l) => `${String(l?.title ?? "")} ${String(l?.code ?? "")} ${String(l?.source ?? "")}`)
    .join(" ")
    .toLowerCase()
  const keyword = /pick\s*-?up|levantamento|em loja|in[- ]store|recolha na loja/.test(hay)
  const pickupInStore = keyword || (lines.length > 0 && !(order.ship_address ?? "").trim())

  return { method, paid, pickupInStore }
}

/** Etiqueta legível da forma de pagamento. */
export function paymentLabel(info: OrderPaymentInfo): string {
  if (info.method) return `${info.method}${info.paid ? " (pago)" : ""}`
  return info.paid ? "Pago" : "—"
}

interface ListResponse<T> {
  data?: T[]
  records?: T[]
}

function asArray<T>(res: ListResponse<T>): T[] {
  return res.data ?? res.records ?? []
}

export async function listOrders(shell: AppShell, limit = 100): Promise<OrderRow[]> {
  // Order SERVER-SIDE by created_at desc. Without it the records API returns
  // an arbitrary `limit` rows (no ORDER BY) — once the table grows past the
  // limit, recent orders fall outside the page and vanish from the app.
  const res = await shell.client.get<ListResponse<OrderRow>>(
    `records/orders?order_by=created_at&order_dir=desc&limit=${limit}`,
  )
  return asArray(res).sort((a, b) => (a.created_at < b.created_at ? 1 : -1))
}

/**
 * Manually close an order (set completed_at) regardless of pipeline state —
 * the flow may have been completed outside the app. Writes via the generic
 * records API (no dedicated action handler) + logs an audit event.
 */
export async function closeOrderManually(shell: AppShell, orderId: string): Promise<void> {
  // Don't null current_step (NOT NULL column) — "Concluída" is driven by
  // completed_at. Leave current_step as-is; just clear the next action.
  await shell.client.patch(`records/orders/${orderId}`, {
    completed_at: new Date().toISOString(),
    next_action: null,
  })
  try {
    await shell.client.post(`records/order_events`, {
      order_id: orderId,
      step: "expedicao",
      tag: "sys",
      title: "Encomenda concluída manualmente",
      description: "Fecho manual — fluxo concluído fora da app.",
    })
  } catch {
    // audit event is best-effort; the close itself already succeeded.
  }
}

export async function listOrderItems(shell: AppShell, orderId: string): Promise<OrderItem[]> {
  // Filter by order_id SERVER-SIDE — fetching a capped page and filtering
  // client-side silently drops rows once the table grows past the limit.
  const res = await shell.client.get<ListResponse<OrderItem>>(
    `records/order_items?order_id=${encodeURIComponent(orderId)}&limit=500`,
  )
  return asArray(res).filter((row) => row.order_id === orderId)
}

/** All order items (across orders) — used to build the reverse SKU→orders
 *  index for the logistics search. Warehouse open-order volume is small
 *  (≤~20 orders), so a single bounded fetch is fine. */
export async function listAllOrderItems(shell: AppShell, limit = 1000): Promise<OrderItem[]> {
  const res = await shell.client.get<ListResponse<OrderItem>>(`records/order_items?limit=${limit}`)
  return asArray(res)
}

/** Mark/unmark a single line as separated (warehouse pick). Writes via the
 *  generic records API — no dedicated action handler needed. */
export async function setLineSeparated(
  shell: AppShell,
  lineId: string,
  on: boolean,
): Promise<void> {
  await shell.client.patch(`records/order_items/${lineId}`, {
    separated_at: on ? new Date().toISOString() : null,
    separated_by: on ? shell.context.userId ?? "logistica" : null,
  })
}

export async function listStockChecks(shell: AppShell, orderId: string): Promise<StockCheck[]> {
  const res = await shell.client.get<ListResponse<StockCheck>>(
    `records/stock_checks?order_id=${encodeURIComponent(orderId)}&limit=500`,
  )
  return asArray(res).filter((row) => row.order_id === orderId)
}

export interface EticdataOrderRow {
  id: string
  order_id: string
  document_number: string | null
  document_type: string | null
  status: string | null
}

/** ETICDATA documents (ENC) keyed by order_id → document_number. Used to
 *  show the ENC number (not the Shopify number) in the logistics screens. */
export async function listEticdataDocNumbers(shell: AppShell): Promise<Map<string, string>> {
  const res = await shell.client.get<ListResponse<EticdataOrderRow>>(`records/eticdata_orders?limit=500`)
  const m = new Map<string, string>()
  for (const r of asArray(res)) {
    if (r.document_number) m.set(r.order_id, r.document_number)
  }
  return m
}

export async function listSupplierOrders(shell: AppShell, orderId: string): Promise<SupplierOrder[]> {
  const res = await shell.client.get<ListResponse<SupplierOrder>>(
    `records/supplier_orders?order_id=${encodeURIComponent(orderId)}&limit=200`,
  )
  return asArray(res).filter((row) => row.order_id === orderId)
}

export async function listSupplierOrderItems(shell: AppShell, orderId: string): Promise<SupplierOrderItem[]> {
  const res = await shell.client.get<ListResponse<SupplierOrderItem>>(
    `records/supplier_order_items?order_id=${encodeURIComponent(orderId)}&limit=500`,
  )
  return asArray(res).filter((row) => row.order_id === orderId)
}

/** The single expedition for an order (UNIQUE order_id). null if none yet. */
export async function loadExpedition(shell: AppShell, orderId: string): Promise<Expedition | null> {
  const res = await shell.client.get<ListResponse<Expedition>>(
    `records/expeditions?order_id=${encodeURIComponent(orderId)}&limit=1`,
  )
  return asArray(res).find((row) => row.order_id === orderId) ?? null
}

/** All expeditions, keyed by tracking_number — used by the Tracking page to
 *  attach the locally-stored R2 label to each Hub tracking row. Warehouse
 *  volume is small, so a single bounded fetch is fine. */
export async function listExpeditionsByTracking(
  shell: AppShell,
  limit = 500,
): Promise<Map<string, Expedition>> {
  const res = await shell.client.get<ListResponse<Expedition>>(
    `records/expeditions?order_by=created_at&order_dir=desc&limit=${limit}`,
  )
  const m = new Map<string, Expedition>()
  for (const e of asArray(res)) {
    if (e.tracking_number) m.set(e.tracking_number, e)
  }
  return m
}

// ─── Ajustes de armazém ──────────────────────────────────────────────────────

export async function listAdjustments(
  shell: AppShell,
  orderId: string,
): Promise<OrderItemAdjustment[]> {
  const res = await shell.client.get<ListResponse<OrderItemAdjustment>>(
    `records/order_item_adjustments?order_id=${encodeURIComponent(orderId)}&order_by=created_at&order_dir=asc&limit=500`,
  )
  return asArray(res).filter((row) => row.order_id === orderId)
}

export async function createAdjustment(
  shell: AppShell,
  input: {
    orderId: string
    orderItemId: string
    qty: number
    status: AdjustmentStatus | string
    comment?: string
    createdByName?: string | null
  },
): Promise<void> {
  await shell.client.post("records/order_item_adjustments", {
    order_id: input.orderId,
    order_item_id: input.orderItemId,
    qty: input.qty,
    status: input.status,
    comment: input.comment?.trim() || null,
    created_by: shell.context.userId ?? null,
    created_by_name: input.createdByName?.trim() || null,
  })
}

export async function deleteAdjustment(shell: AppShell, id: string): Promise<void> {
  await shell.client.delete(`records/order_item_adjustments/${id}`)
}

// ─── Comentários da encomenda ────────────────────────────────────────────────

export async function listComments(shell: AppShell, orderId: string): Promise<OrderComment[]> {
  const res = await shell.client.get<ListResponse<OrderComment>>(
    `records/order_comments?order_id=${encodeURIComponent(orderId)}&order_by=created_at&order_dir=asc&limit=500`,
  )
  return asArray(res).filter((row) => row.order_id === orderId)
}

export async function createComment(
  shell: AppShell,
  orderId: string,
  body: string,
  createdByName?: string | null,
): Promise<void> {
  await shell.client.post("records/order_comments", {
    order_id: orderId,
    body: body.trim(),
    created_by: shell.context.userId ?? null,
    created_by_name: createdByName?.trim() || null,
  })
}

// ─── Utilizadores (para mostrar QUEM ajustou/comentou) ───────────────────────

interface CatalogUserRow {
  iduser: number
  name: string | null
  email: string | null
  auth_user_id: string | null
}

/** Mapa id→nome. O `created_by` guardado é o auth user id do token
 *  (`context.userId`). Indexamos por auth_user_id E por iduser para resolver
 *  seja qual for a chave usada. */
export async function listAppUsers(shell: AppShell): Promise<Map<string, string>> {
  const m = new Map<string, string>()
  try {
    const res = await shell.client.get<{ items?: CatalogUserRow[] }>("catalog/users")
    for (const u of res.items ?? []) {
      const label = u.name || u.email || "—"
      if (u.auth_user_id) m.set(u.auth_user_id, label)
      if (u.iduser != null) m.set(String(u.iduser), label)
    }
  } catch {
    // permissão users.read em falta ou tenant desatualizado — degrada para o id.
  }
  return m
}

/** Nome legível de quem fez a ação (fallback: id truncado). */
export function userLabel(users: Map<string, string>, id: string | null): string {
  if (!id) return "—"
  return users.get(id) ?? id.slice(0, 8)
}

export interface CurrentUser {
  id: string
  name: string | null
  email: string | null
}

/** Utilizador autenticado, via /context — nome resolvido host-side da tabela de
 *  auth GLOBAL (não depende de users.read nem de auth-link do tenant). */
export async function getCurrentUser(shell: AppShell): Promise<CurrentUser> {
  try {
    const res = await shell.client.get<{ data?: { user?: CurrentUser } }>("context")
    const u = res.data?.user
    if (u?.id) return { id: u.id, name: u.name ?? null, email: u.email ?? null }
  } catch {
    /* degrada para o id do token */
  }
  return { id: shell.context.userId ?? "", name: null, email: null }
}

/** Nome a mostrar: o carimbado no write, senão o mapa (linhas antigas), senão id. */
export function authorLabel(
  createdByName: string | null,
  createdBy: string | null,
  users: Map<string, string>,
): string {
  return createdByName?.trim() || userLabel(users, createdBy)
}

export interface WarehousePick {
  id: string
  order_id: string
  email_to: string | null
  /** null = o PDF de separação ainda NÃO foi enviado ao armazém. */
  email_sent_at: string | null
  marked_done_at: string | null
  /** Operador do armazém escolhido no popup de marcar completa. */
  picked_by_name?: string | null
}

/** Setting `warehouse_operators` (um nome por linha) → lista limpa, sem duplicados. */
export function parseWarehouseOperators(raw: unknown): string[] {
  const list = Array.isArray(raw) ? raw.map(String) : typeof raw === "string" ? raw.split(/\r?\n|,/) : []
  return [...new Set(list.map((n) => n.trim()).filter(Boolean))]
}

/** ENC substituída/cancelada por uma alteração no Shopify (anular no ETICDATA). */
export interface EncHistoryRow {
  id: string
  order_id: string
  document_number: string | null
  reason: "edited" | "cancelled" | string
  status: "cancel_pending" | "cancelled_confirmed" | string
  confirmed_at: string | null
  created_at: string
}

export async function listEncHistory(shell: AppShell, orderId: string): Promise<EncHistoryRow[]> {
  try {
    const res = await shell.client.get<ListResponse<EncHistoryRow>>(
      `records/eticdata_order_history?order_id=${encodeURIComponent(orderId)}&limit=50`,
    )
    return asArray(res).filter((r) => r.order_id === orderId)
  } catch {
    // Tabela nova — antes de atualizar o tenant não existe.
    return []
  }
}

/** Registo do envio do PDF de separação (1 por encomenda). null se nunca enviado. */
export async function loadWarehousePick(
  shell: AppShell,
  orderId: string,
): Promise<WarehousePick | null> {
  const res = await shell.client.get<ListResponse<WarehousePick>>(
    `records/warehouse_picks?order_id=${encodeURIComponent(orderId)}&limit=1`,
  )
  return asArray(res).find((r) => r.order_id === orderId) ?? null
}

export interface OrderEventRow {
  id: string
  order_id: string
  step: string
  tag: TimelineEvent["tag"]
  title: string
  description: string | null
  actor: string | null
  occurred_at: string
}

export async function listOrderEvents(shell: AppShell, orderId: string): Promise<TimelineEvent[]> {
  const res = await shell.client.get<ListResponse<OrderEventRow>>(
    `records/order_events?order_id=${encodeURIComponent(orderId)}&limit=500`,
  )
  const rows = asArray(res).filter((row) => row.order_id === orderId)
  rows.sort((a, b) => (a.occurred_at < b.occurred_at ? -1 : 1))
  return rows.map((row) => ({
    id: row.id,
    step: row.step,
    tag: row.tag,
    title: row.title,
    description: row.description,
    occurred_at: row.occurred_at,
    state: "done",
  }))
}

// ─── ZLINK Hub trackings (read-only, via host) ───────────────────────────────

export interface TrackingRow {
  tracking_number: string
  reference: string | null
  status_code: string | null
  status_description: string | null
  active: boolean | null
  provider: string | null
  last_synced_at: string | null
}

export interface TrackingListResult {
  trackings: TrackingRow[]
  pagination: { total: number; limit: number; offset: number }
}

export interface TrackingsFilter {
  status?: "active" | "all"
  limit?: number
  offset?: number
}

/** List expeditions from the ZLINK Hub (paginated). The API key never leaves
 *  the host — this goes through the app action → /api/integrations/run. */
export async function listTrackings(
  shell: AppShell,
  filter: TrackingsFilter = {},
): Promise<TrackingListResult> {
  const res = await invokeAction<TrackingListResult>(shell, "list_trackings", {
    status: filter.status ?? "active",
    limit: filter.limit ?? 50,
    offset: filter.offset ?? 0,
  })
  if (!res.success || !res.data) throw new Error(res.error ?? "Falha a listar expedições")
  return res.data
}

/** Real-time detail for a single tracking number. */
export async function trackShipment(
  shell: AppShell,
  trackingNumber: string,
): Promise<TrackingRow> {
  const res = await invokeAction<{ tracking: TrackingRow }>(shell, "track_shipment", {
    tracking_number: trackingNumber,
  })
  if (!res.success || !res.data?.tracking) throw new Error(res.error ?? "Tracking não encontrado")
  return res.data.tracking
}

export type ActionVerb =
  | "validate_stock"
  | "create_supplier_order"
  | "approve_supplier_order"
  | "create_eticdata_order"
  | "send_warehouse_pdf"
  | "mark_pick_done"
  | "issue_invoice"
  | "create_expedition"
  | "recheck_stock"
  | "create_articles"
  | "skip_supplier_order"
  | "list_trackings"
  | "track_shipment"
  | "update_shopify_fulfillment"
  | "skip_step"
  | "go_back_step"
  | "print_label"
  | "cancel_pickup"
  | "notify_delay"
  | "supplier_order_from_adjustments"
  | "daily_report"
  | "confirm_enc_cancelled"
  | "set_enc_number"
  | "mark_picked_up"
  | "pickup_ready"
  | "shipping_quote_paid"
  | "confirm_enc_updated"
  | "request_shipping_quote"
  | "notify_pickup_ready"
  | "set_payment_method"

export async function invokeAction<T = unknown>(
  shell: AppShell,
  verb: ActionVerb,
  params: Record<string, unknown>,
): Promise<{ success: boolean; data?: T; error?: string; message?: string }> {
  return shell.client.post(`actions/${verb}`, { params })
}

export interface AppConfigField {
  type?: "string" | "number" | "boolean" | "object"
  required?: boolean
  default?: unknown
  description?: string
}

export interface AppConfigResponse {
  config: Record<string, unknown>
  schema: Record<string, AppConfigField>
  readonly: boolean
}

export async function getAppConfig(shell: AppShell): Promise<AppConfigResponse> {
  const res = await shell.client.get<{ success: boolean; data: AppConfigResponse; error?: string }>(
    "config",
  )
  if (!res.success) throw new Error(res.error || "Falha a carregar config")
  return res.data
}

export async function patchAppConfig(
  shell: AppShell,
  patch: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const res = await shell.client.patch<{ success: boolean; data: { config: Record<string, unknown> }; error?: string }>(
    "config",
    { patch },
  )
  if (!res.success) throw new Error(res.error || "Falha a guardar config")
  return res.data.config
}

export interface LineChange {
  sku: string
  before: number | null
  after: number | null
}

/** Mapa SKU (minúsculas) → alteração da última edição do Shopify. */
export function parseLineChanges(order: Pick<OrderRow, "line_changes"> | null | undefined): Map<string, LineChange> {
  let raw = order?.line_changes
  if (typeof raw === "string") {
    try {
      raw = JSON.parse(raw)
    } catch {
      raw = null
    }
  }
  const m = new Map<string, LineChange>()
  if (raw && typeof raw === "object") {
    for (const [k, v] of Object.entries(raw as Record<string, LineChange>)) m.set(k.toLowerCase(), v)
  }
  return m
}

/** Formas de pagamento para as encomendas do ETICDATA (espelho do engine). */
export const MANUAL_PAYMENT_METHODS = [
  "Transferência bancária",
  "MB WAY",
  "Multibanco",
  "Numerário",
  "Cartão",
  "Cheque",
  "Outro",
]

/** Dados do cliente em falta numa encomenda do ETICDATA (espelho de engine/orders.ts). */
export function missingCustomerData(order: OrderRow): string[] {
  if (!isErpOrder(order)) return []
  const missing: string[] = []
  if (!resolvePaymentInfo(order).pickupInStore) {
    if (!String(order.ship_address ?? "").trim()) missing.push("morada")
    if (!String(order.ship_zip ?? "").trim()) missing.push("código postal")
  }
  if (!String(order.ship_phone ?? order.bill_phone ?? "").trim()) missing.push("telefone")
  if (!String(order.customer_email ?? "").trim()) missing.push("email")
  return missing
}

/** Linha com a quantidade que se separa e valoriza: a exata do ERP (1,5) quando existe. */
export function withExactQty<T extends { qty: number; qty_exact?: number | string | null }>(it: T): T {
  const exact = it.qty_exact != null && it.qty_exact !== "" ? Number(it.qty_exact) : NaN
  return Number.isFinite(exact) ? { ...it, qty: exact } : it
}
